import { createEmbedThemeBridge } from './agents-embed-theme';

/** The embedded editor has no credentials or independent organization selection. */
export const isAgentsEmbed = () => window.location.pathname.startsWith('/embed/');

type Pending = { resolve: (value: unknown) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> };
export function createEmbedTransport(host: Window, parent: Window, origin: string, channel: string) {
  const pending = new Map<string, Pending>();
  const receive = (event: MessageEvent) => {
    const msg = event.data;
    if (event.origin !== origin || event.source !== parent || !msg || msg.channel !== channel || msg.type !== 'fyso.agents.response') return;
    const request = pending.get(msg.id);
    if (!request || typeof msg.status !== 'number') return;
    pending.delete(msg.id);
    clearTimeout(request.timer);
    if (msg.status >= 200 && msg.status < 300) request.resolve(msg.payload?.data);
    else {
      const error = msg.payload?.error;
      request.reject(Object.assign(new Error(typeof error === 'string' ? error : error?.message || 'Agent editor request failed'), {
        status: msg.status, code: msg.payload?.code ?? error?.code,
      }));
    }
  };
  let suspended = false;
  const suspend = () => {
    suspended = true;
    host.removeEventListener('message', receive);
    for (const request of pending.values()) { clearTimeout(request.timer); request.reject(new Error('Agent editor closed')); }
    pending.clear();
  };
  const resume = () => {
    suspended = false;
    host.addEventListener('message', receive);
  };
  host.addEventListener('message', receive);
  host.addEventListener('pagehide', suspend);
  host.addEventListener('pageshow', resume);
  return {
    request(path: string, options: RequestInit = {}): Promise<unknown> {
      if (suspended) return Promise.reject(new Error('Agent editor closed'));
      const id = crypto.randomUUID();
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => { pending.delete(id); reject(new Error('Agent editor request timed out')); }, 120_000);
        pending.set(id, { resolve, reject, timer });
        parent.postMessage({ type: 'fyso.agents.request', channel, id, path, method: options.method ?? 'GET',
          ...(options.body ? { body: JSON.parse(String(options.body)) } : {}) }, origin);
      });
    },
    close() { parent.postMessage({ type: 'fyso.agents.close', channel }, origin); },
    dispose() {
      suspend();
      host.removeEventListener('pagehide', suspend);
      host.removeEventListener('pageshow', resume);
    },
  };
}

const initialSearch = new URLSearchParams(window.location.search);
export function embedSearch(search: string) {
  const params = new URLSearchParams(search);
  for (const key of ['parentOrigin', 'channel']) {
    const value = initialSearch.get(key);
    if (value) params.set(key, value);
  }
  return `?${params.toString()}`;
}

let transport: ReturnType<typeof createEmbedTransport> | undefined;
export function embedTransport() {
  if (transport) return transport;
  const params = new URLSearchParams(window.location.search);
  const origin = params.get('parentOrigin') ?? '';
  const channel = params.get('channel') ?? '';
  const allowed = ['https://fyso.biz', 'https://www.fyso.biz'];
  if (import.meta.env.DEV) allowed.push('http://localhost:3000', 'http://localhost:3100', 'http://127.0.0.1:3100', 'http://localhost:3101', 'http://127.0.0.1:3101');
  if (window.parent === window || !allowed.includes(origin) || !/^[a-zA-Z0-9-]{16,64}$/.test(channel)) {
    throw new Error('The agent editor must be opened from Fyso Business');
  }
  transport = createEmbedTransport(window, window.parent, origin, channel);
  createEmbedThemeBridge(window, window.parent, origin, channel);
  return transport;
}
